import { ApiState } from '@/models/generics';
import { TWishlistProduct } from '@/services/wishlist/models/wishlist';
import { useLencoSelector } from '@/hooks/useLencoSelector';
import { useLencoDispatch } from './useLencoDispatch';
import {
	wishlistLoading,
	wishlistSuccess,
	wishlistError,
} from '@/store/wishlist/wishlistSlice';
import { wishlistService } from '@/services/wishlist';
import { keyBy } from 'lodash';

type TUseWishlist = {
	getWishlistApiState: ApiState;
	wishlist: any;
	wishlistedProductsMap?: Record<number, TWishlistProduct>;
	wishlistActions: {
		getWishlists: () => void;
		addItemToWishList: (catalogId: number, query?: any) => Promise<any>;
		removeItemFromWishList: (catalogId: number) => Promise<any>;
		isProductWishlisted: (catalogId: number) => boolean;
	};
};

export function useWishlist(query?: any): TUseWishlist {
	const { wishlist, wishlistedProductsMap, getWishlistApiState } =
		useLencoSelector((store) => ({
			wishlist: store.wishlist.wishlists,
			wishlistedProductsMap: store.wishlist.wishlistedProductsMap,
			getWishlistApiState: store.wishlist.getWishlistApiState,
		}));
	const dispatch = useLencoDispatch();

	async function getWishlistsAction() {
		try {
			dispatch(wishlistLoading());
			const lineOfProduct = window.location.pathname.includes('unlmtd')
				? 'UNLMTD'
				: undefined;
			const queryParams = lineOfProduct ? { ...query, lineOfProduct } : query;

			const res = await wishlistService.getWishlists(queryParams);
			const wishlistedProducts = res.data?.data?.products ?? [];
			const wishlistedProductsMap = keyBy(
				wishlistedProducts,
				(product: TWishlistProduct) => product.id
			);

			dispatch(
				wishlistSuccess({
					wishlistedProducts,
					wishlistedProductsMap,
				})
			);
		} catch (error) {
			dispatch(wishlistError());
		}
	}

	async function addItemToWishList(catalogId: number, additionalQuery?: any) {
		try {
			const response = await wishlistService.addToWishList(
				catalogId,
				additionalQuery
			);
			await getWishlistsAction();
			return response;
		} catch (error) {
			console.error('Error adding item to wishlist:', error);
			throw error;
		}
	}

	async function removeItemFromWishList(catalogId: number) {
		try {
			const response = await wishlistService.deleteFromWishlist(catalogId);
			await getWishlistsAction();
			return response;
		} catch (error) {
			console.error('Error removing item from wishlist:', error);
			throw error;
		}
	}

	function isProductWishlisted(catalogId: number) {
		return !!(
			wishlistedProductsMap && wishlistedProductsMap.hasOwnProperty(catalogId)
		);
	}

	return {
		wishlist,
		getWishlistApiState,
		wishlistedProductsMap,
		wishlistActions: {
			getWishlists: getWishlistsAction,
			addItemToWishList,
			removeItemFromWishList,
			isProductWishlisted,
		},
	};
}
